import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Input,
  FormGroup,
  Label,
  Button,
} from "reactstrap";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Triangle } from "react-loader-spinner";
import PurchaseServices from "../../../Services/QRCodeServices/PurchaseServices/PurchaseServices";
import NumberCommas from "../../../Components/NumberCommas/NumberCommas";
import PurchaseAddEditModal from "../Components/PurchaseAddEditModal";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";
import PurchaseDetailModal from "../Components/PurchaseDetailModal";

function Purchase() {
  const [billsList, setBillsList] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [detailModal, setDetailModal] = useState(false); // State for detail modal
  const [selectedPurchaseId, setSelectedPurchaseId] = useState(null); // Track selected purchase ID

  const toggleModal = () => setModal(!modal);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);
  const toggleDetailModal = () => setDetailModal(!detailModal);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Item Name",
      selector: (row) => row.itemName,
      sortable: true,
    },
    {
      name: "Quantity",
      selector: (row) => row.quantity,
      sortable: true,
      cell: (row) => <div className="w-100 text-end">{row.quantity}</div>,
      width: "120px",
      right: true,
    },
    {
      name: "Price",
      selector: (row) => row.price,
      sortable: true,
      cell: (row) => (
        <div className="w-100 text-end">
          <NumberCommas number={row.price} />
        </div>
      ),
      width: "120px",
      right: true,
    },
    {
      name: "Payment Status",
      selector: (row) => (row.isPurchaseBillPaid ? "Paid" : "Unpaid"),
      sortable: true,
      center: true,
      cell: (row) => (
        <Badge color={row.isPurchaseBillPaid ? "success" : "warning"}>
          {row.isPurchaseBillPaid ? "Paid" : "Unpaid"}
        </Badge>
      ),
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <div className="hstack gap-2">
          <Button
            color="btn btn-soft-info"
            size="sm"
            onClick={() => handleItemNameClick(row)}
          >
            <i className="ri-eye-line"></i>
          </Button>
          <Button
            color="btn btn-soft-warning"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            <i className="ri-edit-line"></i>
          </Button>
          <Button
            color="btn btn-soft-danger"
            size="sm"
            onClick={() => handleDeleteClick(row)}
          >
            <i className="ri-delete-bin-line"></i>
          </Button>
        </div>
      ),
    },
  ];

  async function fetchPurchaseBills() {
    try {
      setLoading(true);
      const fetchedBills = await PurchaseServices.getList();
      setBillsList(fetchedBills);
      setFilteredBills(fetchedBills);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching purchase bills:", err);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchPurchaseBills();
  }, []);

  useEffect(() => {
    let filtered = billsList;

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((bill) =>
        bill.itemName?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    if (paymentStatusFilter) {
      filtered = filtered.filter(
        (bill) =>
          (paymentStatusFilter === "Paid" && bill.isPurchaseBillPaid) ||
          (paymentStatusFilter === "Unpaid" && !bill.isPurchaseBillPaid)
      );
    }

    setFilteredBills(filtered);
  }, [searchTerm, paymentStatusFilter, billsList]);

  const handleEdit = (row) => {
    setIsEdit(true);
    setSelectedBill(row);
    toggleModal();
  };

  const handleCreate = () => {
    setIsEdit(false);
    setSelectedBill(null);
    toggleModal();
  };

  const handleModalSuccess = () => {
    fetchPurchaseBills();
    toggleModal();
  };

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDeleteModal();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await PurchaseServices.delete(selectedDeleteItem.id); // Assuming delete method exists
        toast.success("Purchase bill deleted successfully");
        fetchPurchaseBills();
      } catch (error) {
        toast.error("Failed to delete purchase bill");
      } finally {
        toggleDeleteModal();
        setSelectedDeleteItem(null);
      }
    }
  };

  const handleItemNameClick = (row) => {
    setSelectedPurchaseId(row.id); // Set the selected purchase ID
    toggleDetailModal(); // Open the detail modal
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Purchase Bills List" pageTitle="Purchase Bills" />
          <Row>
            <Col>
              <Card>
                <CardHeader>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <FormGroup className="col-lg-3">
                      <Input
                        type="text"
                        placeholder="Search by Item Name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormGroup>
                    <FormGroup className="col-lg-3">
                      <Input
                        type="select"
                        value={paymentStatusFilter}
                        onChange={(e) => setPaymentStatusFilter(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="Paid">Paid</option>
                        <option value="Unpaid">Unpaid</option>
                      </Input>
                    </FormGroup>
                    <div className="ms-auto">
                      <Button color="primary" onClick={handleCreate}>
                        + Create Purchase
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <DataTable
                    columns={columns}
                    data={filteredBills}
                    customStyles={customStyles}
                    responsive
                    striped
                    highlightOnHover
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    progressPending={loading}
                    progressComponent={
                      <div className="my-3">
                        <Triangle
                          visible={true}
                          height="80"
                          width="80"
                          color="#5B71B9"
                          ariaLabel="triangle-loading"
                        />
                        <h5 className="mt-1">Loading...</h5>
                      </div>
                    }
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <PurchaseDetailModal
        isOpen={detailModal}
        toggle={toggleDetailModal}
        purchaseId={selectedPurchaseId}
      />

      <PurchaseAddEditModal
        isOpen={modal}
        toggle={toggleModal}
        isEdit={isEdit}
        selectedBill={selectedBill}
        onSuccess={handleModalSuccess}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={toggleDeleteModal}
        selectedItem={selectedDeleteItem}
      />
    </React.Fragment>
  );
}

export default Purchase;
